/**
 * Funil de vendas (P2 #159) — fachada sobre `apiService`.
 * Mantém as chamadas tipadas e em um único lugar; importar daqui nas views
 * facilita mocks e testes e2e.
 */
import { apiService } from './api';

export type Deal = {
  id: string;
  tenantId: string;
  projectId: string | null;
  pipelineId: string;
  stageId: string;
  contactId: string | null;
  ownerId: string | null;
  conversationId: string | null;
  title: string;
  amount: string | number; // vem como string no Prisma Decimal
  currency: string;
  expectedCloseAt: string | null;
  closedAt: string | null;
  origin: string | null;
  status: 'OPEN' | 'WON' | 'LOST';
  position: number;
  createdAt: string;
  updatedAt: string;
  contact?: { id: string; nome: string; telefone: string | null } | null;
  owner?: { id: string; nome: string } | null;
  stage?: { id: string; name: string; probability: number; isWon: boolean; isLost: boolean } | null;
  pipeline?: { id: string; name: string } | null;
};

export type Stage = {
  id: string;
  pipelineId: string;
  name: string;
  position: number;
  probability: number;
  color: string | null;
  isWon: boolean;
  isLost: boolean;
};

export type Pipeline = {
  id: string;
  name: string;
  isDefault: boolean;
  projectId: string | null;
  stages: Stage[];
};

export type FunnelTask = {
  id: string;
  dealId: string;
  ownerId: string | null;
  title: string;
  description: string | null;
  dueAt: string | null;
  doneAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FunnelActivity = {
  id: string;
  dealId: string;
  kind: 'stage_change' | 'note' | 'message' | 'task' | 'system';
  payload: Record<string, unknown>;
  actorId: string | null;
  createdAt: string;
};

export const funnelApi = {
  pipelines: (projectId?: string | null) => apiService.listFunnelPipelines(projectId),
  deals: (filters?: Parameters<typeof apiService.listFunnelDeals>[0]) => apiService.listFunnelDeals(filters ?? {}),
  deal: (id: string) => apiService.getFunnelDeal(id),
  createDeal: (data: Parameters<typeof apiService.createFunnelDeal>[0]) => apiService.createFunnelDeal(data),
  updateDeal: (id: string, data: Parameters<typeof apiService.updateFunnelDeal>[1]) => apiService.updateFunnelDeal(id, data),
  moveDeal: (id: string, stageId: string, position?: number) => apiService.moveFunnelDeal(id, stageId, position),
  tasks: (dealId: string) => apiService.listFunnelDealTasks(dealId),
  createTask: (dealId: string, data: { title: string; description?: string | null; dueAt?: string | null }) =>
    apiService.createFunnelDealTask(dealId, data),
  updateTask: (taskId: string, data: Partial<{ title: string; description: string | null; dueAt: string | null; doneAt: string | null }>) =>
    apiService.updateFunnelTask(taskId, data),
  deleteTask: (taskId: string) => apiService.deleteFunnelTask(taskId),
  activities: (dealId: string) => apiService.listFunnelDealActivities(dealId),
  addActivity: (dealId: string, kind: 'note' | 'message' | 'task', payload: Record<string, unknown> = {}) =>
    apiService.addFunnelDealActivity(dealId, kind, payload),
};